import React from 'react';

import { AccountType, IAccount } from 'dto/access-management/account-dto';
import { IDisplayDeploymentGroup } from 'dto/deployment/deployment-group-dto';
import { BasicForm, IFormItemValidation, ITreeSelectDataSource, TextFieldItem, useForm } from 'ui-lib';
import { OrganisationSelectItem } from 'pages/private/components/organisation-select';
import { SearchableSelectFormItem } from 'pages/private/components/searchable-component';
import { buildLabelRule } from 'utils/common-utils';

interface ISelectDeploymentStepProps {
    currentUserAccountType: AccountType | string;
    accountTreeDataSource: ITreeSelectDataSource;
    selectedAccount?: IAccount;
    deploymentGroups: IDisplayDeploymentGroup[];
    selectedDeploymentGroup?: IDisplayDeploymentGroup;
    deploymentName: string;
    deploymentGroupValidation: IFormItemValidation;
    onAccountChange: (accountId: number) => void;
    onDeploymentGroupChange: (deploymentGroupId: number) => void;
    onDeploymentNameChange: (value: string) => void;
}

export const deploymentLabelRule = buildLabelRule('Deployment name');

export const isValidDeploymentLabel = (label: string): boolean =>
    !!label && new RegExp(deploymentLabelRule.pattern).test(label);

const CODES = {
    accountField: 'selectAccount',
    deploymentGroupField: 'deploymentGroupItem',
    deploymentNameField: 'newDeploymentName'
};

export const SelectDeploymentStep = (props: ISelectDeploymentStepProps): JSX.Element => {
    const [form] = useForm();

    // Form items only read initialValue on mount, so push prop changes into the form store
    React.useEffect(() => {
        form.setFieldsValue({
            [CODES.accountField]: props.selectedAccount?.id,
            [CODES.deploymentGroupField]: props.selectedDeploymentGroup?.value,
        });
    }, [props.selectedAccount?.id, props.selectedDeploymentGroup?.value]);

    const deploymentGroupSelectOptions = props.deploymentGroups.map((group) => ({
        ...group,
        label: group.displayLabel,
    }));

    return (
        <BasicForm
            key={'deployment-form'}
            form={form}
            cardPros={{ bordered: false }}
            items={[
                props.currentUserAccountType === AccountType.DPU && (
                    <OrganisationSelectItem
                        code={CODES.accountField}
                        label='Account'
                        labelAlign='right'
                        selectProps={{
                            treeDataSource: props.accountTreeDataSource,
                            value: props.selectedAccount?.id,
                            placeholder: 'Select Account',
                            expandAll: true,
                            onChange: props.onAccountChange,
                        }}
                    />
                ),
                <SearchableSelectFormItem
                    code={CODES.deploymentGroupField}
                    labelInValue={true}
                    label='Deployment group'
                    labelAlign='right'
                    placeholder='Select a deployment group'
                    dataSource={deploymentGroupSelectOptions}
                    selectedValue={props.selectedDeploymentGroup?.value}
                    onChange={(group: { value: number }): void => props.onDeploymentGroupChange(group.value)}
                    customizeValidate={props.deploymentGroupValidation}
                />,
                <TextFieldItem
                    code={CODES.deploymentNameField}
                    label='Deployment name'
                    labelAlign='right'
                    rule={deploymentLabelRule}
                    initialValue={props.deploymentName}
                    onChange={(event): void => props.onDeploymentNameChange(event.target.value)}
                />,
            ]}
        />
    );
};
